import React from "react";
import { createGuid } from "../utilities";
import {
    to01Null,
    normalizeDatabases,
    normalizePopulationData,
    normalizeSurveyData,
    normalizeEmuData,
    synchonizeContraceptivePrevalence,
} from "../data";
import * as api from "../api/api";
import { withErrorBoundary } from "../errors";
import { withTranslation } from "react-i18next";
import PrepareData from "../pages/PrepareData";

const createInitialState = () => ({
    loadingData: false,
    databases: undefined,
    defaultCountries: undefined,

    selectedSurveyDatabaseId: undefined,
    selectedSurveyDataCountryNumericCode: undefined,
    surveyData: undefined,
    newSurveyDatabaseName: undefined,
    loadingSurveyData: false,
    savingSurveyDatabase: false,

    selectedPopulationDatabaseId: undefined,
    selectedPopulationDataCountryNumericCode: undefined,
    populationData: undefined,
    newPopulationDatabaseName: undefined,
    loadingPopulationData: false,
    savingPopulationDatabase: false,

    selectedEmuDatabaseId: "",
    selectedEmuDataCountryNumericCode: undefined,
    emuData: undefined,
    newEmuDatabaseName: undefined,
    loadingEmuData: false,
    savingEmuDatabase: false,

    notificationVariant: undefined,
    notificationOpen: false,
    notificationMessage: undefined,
});

const findDefaultDatabaseId = (databases) => {
    if (!databases) {
        return;
    }

    for (let databaseId of Object.keys(databases)) {
        const { type, isDefault } = databases[databaseId];

        if (type === "survey" && isDefault) {
            return databaseId;
        }
    }
};

const getCountries = (databases, databaseId) => {
    if (!databases || !databaseId) {
        return;
    }

    const database = databases[databaseId];

    return Object.keys(database.divisions).reduce((result, divisionKey) => {
        const division = database.divisions[divisionKey];

        if (division.isCountry) {
            result[divisionKey] = division;
        }

        return result;
    }, {});
};

const findIndex = (data, key, value) => {
    return data.map((datum) => datum[key]).indexOf(value);
};

const PrepareDataContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        setStateSafely({
            loadingData: true,
        });

        api.fetchDatabases(signal.token)
            .then((response) => {
                if (!response) {
                    return;
                }

                const databases = normalizeDatabases(response.data);

                setStateSafely({
                    databases: databases,
                    defaultCountries: getCountries(databases, findDefaultDatabaseId(databases)),
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingData: false,
                });
            });

        return () => {
            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _findDefaultDatabaseId = () => findDefaultDatabaseId(state.databases);

    const _getCountries = (databaseId) => getCountries(state.databases, databaseId);

    const createDatabasePayload = (type, name, databaseId, divisionNumericCode, data) => {
        const payload = {
            id: createGuid(),
            type: type,
            databaseId: databaseId || _findDefaultDatabaseId(),
            divisionNumericCode: divisionNumericCode,
            name: name,
            data: data.map((datum) => ({
                ...datum,
                divisionNumericCode,
            })),
        };

        return payload;
    };

    const createDatabase = (databasePayload) => ({
        type: databasePayload.type,
        name: databasePayload.name,
        divisions: {
            [databasePayload.divisionNumericCode]:
                state.databases[databasePayload.databaseId].divisions[databasePayload.divisionNumericCode],
        },
    });

    //==========================
    // Survey database
    //==========================

    const onSelectSurveyDatabase = (databaseId) => {
        const countries = _getCountries(databaseId);
        const numericCode = Object.keys(countries).length !== 1 ? undefined : parseInt(Object.keys(countries)[0], 10);

        setStateSafely({
            selectedSurveyDatabaseId: databaseId,
            selectedSurveyDataCountryNumericCode: numericCode,
        });
    };

    const onSelectSurveyDataCountry = (countryNumericCode) => {
        setStateSafely({
            selectedSurveyDataCountryNumericCode: countryNumericCode,
        });
    };

    const onLoadCountrySurveyData = () => {
        setStateSafely({
            loadingSurveyData: true,
        });

        const runType = "All";

        api.fetchSurveyDatabase(
            state.selectedSurveyDatabaseId,
            state.selectedSurveyDataCountryNumericCode,
            undefined,
            runType,
            signal.token
        )
            .then((response) => {
                if (!response) {
                    return;
                }

                const surveyData = normalizeSurveyData(response.data).map((datum) => ({
                    ...datum,
                    id: createGuid(),
                    include: !datum.exclude,
                }));

                setStateSafely({
                    surveyData: surveyData,
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingSurveyData: false,
                });
            });
    };

    const onAddNewSurveyItem = (item) => {
        setStateSafely({
            surveyData: [...(state.surveyData || []), item],
        });
    };

    const onEditSurveyItem = (item, updatedField, callback) => {
        const surveyData = [...state.surveyData];

        const itemIndex = findIndex(surveyData, "id", item.id);

        // noinspection JSValidateTypes
        surveyData[itemIndex] = synchonizeContraceptivePrevalence(item, updatedField);

        setStateSafely(
            {
                surveyData: surveyData,
            },
            callback
        );
    };

    const onDeleteSurveyItem = (itemId) => {
        setStateSafely({
            surveyData: state.surveyData.filter((datum) => datum.id !== itemId),
        });
    };

    const onClearSurveyData = () => {
        setStateSafely({
            surveyData: undefined,
        });
    };

    const onChangeSurveyDatabaseName = (databaseName) => {
        setStateSafely({
            newSurveyDatabaseName: databaseName,
        });
    };

    const onSaveSurveyDatabase = () => {
        setStateSafely({
            savingSurveyDatabase: true,
        });

        const databasePayload = createDatabasePayload(
            "survey",
            state.newSurveyDatabaseName,
            state.selectedSurveyDatabaseId,
            state.selectedSurveyDataCountryNumericCode,
            state.surveyData.map(({ include, ...rest }) => ({
                ...rest,
                possible_outlier: to01Null(rest.possible_outlier),
                possible_outlier_userinput: to01Null(rest.possible_outlier_userinput),
                hasGeographicalRegionBias: !!rest.geographicalRegionBiasReason,
                exclude: !include,
            }))
        );

        api.saveDatabase(databasePayload, signal.token)
            .then((response) => {
                const { id } = response.data;

                const databases = {
                    ...state.databases,
                    [id]: createDatabase(databasePayload),
                };

                setStateSafely({
                    databases: databases,
                    notificationVariant: "success",
                    notificationOpen: true,
                    notificationMessage: props.t('Database "{{databaseName}}" was saved', {
                        databaseName: databasePayload.name,
                    }),
                });
            })
            .finally(() => {
                setStateSafely({
                    savingSurveyDatabase: false,
                });
            });
    };

    //==========================
    // Population database
    //==========================

    const onSelectPopulationDatabase = (databaseId) => {
        const countries = _getCountries(databaseId);
        const numericCode = Object.keys(countries).length !== 1 ? undefined : parseInt(Object.keys(countries)[0], 10);

        setStateSafely({
            selectedPopulationDatabaseId: databaseId,
            selectedPopulationDataCountryNumericCode: numericCode,
        });
    };

    const onSelectPopulationDataCountry = (countryNumericCode) => {
        setStateSafely({
            selectedPopulationDataCountryNumericCode: countryNumericCode,
        });
    };

    const onLoadCountryPopulationData = () => {
        setStateSafely({
            loadingPopulationData: true,
        });

        api.fetchPopulationDatabase(
            state.selectedPopulationDatabaseId,
            state.selectedPopulationDataCountryNumericCode,
            signal.token
        )
            .then((response) => {
                if (!response) {
                    return;
                }

                const populationData = normalizePopulationData(
                    response.data.filter(
                        (datum) =>
                            datum.divisionNumericCode === state.selectedPopulationDataCountryNumericCode &&
                            datum.ageRange === "15-49"
                    )
                ).map((datum) => ({
                    ...datum,
                    id: createGuid(),
                }));

                setStateSafely({
                    populationData: populationData,
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingPopulationData: false,
                });
            });
    };

    const onAddNewPopulationItem = (item) => {
        setStateSafely({
            populationData: [...(state.populationData || []), item],
        });
    };

    const onEditPopulationItem = (item) => {
        const populationData = [...state.populationData];

        const itemIndex = findIndex(populationData, "id", item.id);
        populationData[itemIndex] = item;

        setStateSafely({
            populationData: populationData,
        });
    };

    const onDeletePopulationItem = (itemId) => {
        setStateSafely({
            populationData: state.populationData.filter((datum) => datum.id !== itemId),
        });
    };

    const onClearPopulationData = () => {
        setStateSafely({
            populationData: undefined,
        });
    };

    const onChangePopulationDatabaseName = (databaseName) => {
        setStateSafely({
            newPopulationDatabaseName: databaseName,
        });
    };

    const onSavePopulationDatabase = () => {
        setStateSafely({
            savingPopulationDatabase: true,
        });

        const databasePayload = createDatabasePayload(
            "population",
            state.newPopulationDatabaseName,
            state.selectedPopulationDatabaseId,
            state.selectedPopulationDataCountryNumericCode,
            state.populationData
        );

        api.saveDatabase(databasePayload, signal.token)
            .then((response) => {
                const { id } = response.data;

                const databases = {
                    ...state.databases,
                    [id]: createDatabase(databasePayload),
                };

                setStateSafely({
                    databases: databases,
                    notificationVariant: "success",
                    notificationOpen: true,
                    notificationMessage: props.t('Database "{{databaseName}}" was saved', {
                        databaseName: databasePayload.name,
                    }),
                });
            })
            .finally(() => {
                setStateSafely({
                    savingPopulationDatabase: false,
                });
            });
    };

    //==========================
    // EMU database
    //==========================

    const onSelectEmuDatabase = (databaseId) => {
        const countries = _getCountries(databaseId || _findDefaultDatabaseId());
        const numericCode = Object.keys(countries).length !== 1 ? undefined : parseInt(Object.keys(countries)[0], 10);

        setStateSafely({
            selectedEmuDatabaseId: databaseId,
            selectedEmuDataCountryNumericCode: numericCode,
        });
    };

    const onSelectEmuDataCountry = (countryNumericCode) => {
        setStateSafely({
            selectedEmuDataCountryNumericCode: countryNumericCode,
        });
    };

    const onLoadCountryEmuData = () => {
        setStateSafely({
            loadingEmuData: true,
        });

        api.fetchEmuDatabase(state.selectedEmuDatabaseId, state.selectedEmuDataCountryNumericCode, signal.token)
            .then((response) => {
                if (!response) {
                    return;
                }

                const emuData = normalizeEmuData(
                    response.data.filter(
                        (datum) => datum.divisionNumericCode === state.selectedEmuDataCountryNumericCode
                    )
                ).map((datum) => ({
                    ...datum,
                    id: createGuid(),
                }));

                setStateSafely({
                    emuData: emuData,
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingEmuData: false,
                });
            });
    };

    const onAddNewEmuItem = (item) => {
        setStateSafely({
            emuData: [...(state.emuData || []), item],
        });
    };

    const onEditEmuItem = (item) => {
        const emuData = [...state.emuData];

        const itemIndex = findIndex(emuData, "id", item.id);
        emuData[itemIndex] = item;

        setStateSafely({
            emuData: emuData,
        });
    };

    const onDeleteEmuItem = (itemId) => {
        setStateSafely({
            emuData: state.emuData.filter((datum) => datum.id !== itemId),
        });
    };

    const onClearEmuData = () => {
        setStateSafely({
            emuData: undefined,
        });
    };

    const onChangeEmuDatabaseName = (databaseName) => {
        setStateSafely({
            newEmuDatabaseName: databaseName,
        });
    };

    const onSaveEmuDatabase = () => {
        setStateSafely({
            savingEmuDatabase: true,
        });

        const databasePayload = createDatabasePayload(
            "emu",
            state.newEmuDatabaseName,
            state.selectedEmuDatabaseId || _findDefaultDatabaseId(),
            state.selectedEmuDataCountryNumericCode,
            state.emuData.map(({ startYear, endYear, ...rest }) => ({
                ...rest,
                year: startYear,
            }))
        );

        api.saveDatabase(databasePayload, signal.token)
            .then((response) => {
                const { id } = response.data;

                const databases = {
                    ...state.databases,
                    [id]: createDatabase(databasePayload),
                };

                setStateSafely({
                    databases: databases,
                    notificationVariant: "success",
                    notificationOpen: true,
                    notificationMessage: props.t('Database "{{databaseName}}" was saved', {
                        databaseName: databasePayload.name,
                    }),
                });
            })
            .finally(() => {
                setStateSafely({
                    savingEmuDatabase: false,
                });
            });
    };

    //==========================
    // Notification
    //==========================

    const onCloseNotification = () => {
        setStateSafely({
            notificationVariant: undefined,
            notificationOpen: false,
            notificationMessage: undefined,
        });
    };

    return (
        <PrepareData
            match={props.match}
            databases={state.databases}
            surveyDatabaseCountries={_getCountries(state.selectedSurveyDatabaseId)}
            selectedSurveyDatabaseId={state.selectedSurveyDatabaseId}
            selectedSurveyDataCountryNumericCode={state.selectedSurveyDataCountryNumericCode}
            surveyData={state.surveyData}
            newSurveyDatabaseName={state.newSurveyDatabaseName}
            populationDatabaseCountries={_getCountries(state.selectedPopulationDatabaseId)}
            selectedPopulationDatabaseId={state.selectedPopulationDatabaseId}
            selectedPopulationDataCountryNumericCode={state.selectedPopulationDataCountryNumericCode}
            populationData={state.populationData}
            newPopulationDatabaseName={state.newPopulationDatabaseName}
            emuDatabaseCountries={_getCountries(state.selectedEmuDatabaseId || _findDefaultDatabaseId())}
            selectedEmuDatabaseId={state.selectedEmuDatabaseId}
            selectedEmuDataCountryNumericCode={state.selectedEmuDataCountryNumericCode}
            emuData={state.emuData}
            newEmuDatabaseName={state.newEmuDatabaseName}
            notificationVariant={state.notificationVariant}
            notificationOpen={state.notificationOpen}
            notificationMessage={state.notificationMessage}
            onSelectSurveyDatabase={onSelectSurveyDatabase}
            onSelectSurveyDataCountry={onSelectSurveyDataCountry}
            onLoadCountrySurveyData={onLoadCountrySurveyData}
            onAddNewSurveyItem={onAddNewSurveyItem}
            onEditSurveyItem={onEditSurveyItem}
            onDeleteSurveyItem={onDeleteSurveyItem}
            onClearSurveyData={onClearSurveyData}
            onChangeSurveyDatabaseName={onChangeSurveyDatabaseName}
            onSaveSurveyDatabase={onSaveSurveyDatabase}
            onSelectPopulationDatabase={onSelectPopulationDatabase}
            onSelectPopulationDataCountry={onSelectPopulationDataCountry}
            onLoadCountryPopulationData={onLoadCountryPopulationData}
            onAddNewPopulationItem={onAddNewPopulationItem}
            onEditPopulationItem={onEditPopulationItem}
            onDeletePopulationItem={onDeletePopulationItem}
            onClearPopulationData={onClearPopulationData}
            onChangePopulationDatabaseName={onChangePopulationDatabaseName}
            onSavePopulationDatabase={onSavePopulationDatabase}
            onSelectEmuDatabase={onSelectEmuDatabase}
            onSelectEmuDataCountry={onSelectEmuDataCountry}
            onLoadCountryEmuData={onLoadCountryEmuData}
            onAddNewEmuItem={onAddNewEmuItem}
            onEditEmuItem={onEditEmuItem}
            onDeleteEmuItem={onDeleteEmuItem}
            onClearEmuData={onClearEmuData}
            onChangeEmuDatabaseName={onChangeEmuDatabaseName}
            onSaveEmuDatabase={onSaveEmuDatabase}
            onCloseNotification={onCloseNotification}
            loadingData={state.loadingData}
            loadingSurveyData={state.loadingSurveyData}
            loadingPopulationData={state.loadingPopulationData}
            loadingEmuData={state.loadingEmuData}
            savingSurveyDatabase={state.savingSurveyDatabase}
            savingPopulationDatabase={state.savingPopulationDatabase}
            savingEmuDatabase={state.savingEmuDatabase}
        />
    );
};

export default withTranslation()(withErrorBoundary(PrepareDataContainer));
